import { useEffect, useState } from "react";
import type { VisibilityState } from "@tanstack/react-table";

function readVisibility(
  storageKey: string,
  fallback: VisibilityState,
  forcedVisibleColumnIds: string[],
): VisibilityState {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? (JSON.parse(raw) as VisibilityState) : {};
    const next = { ...fallback, ...parsed };
    for (const id of forcedVisibleColumnIds) {
      next[id] = true;
    }
    return next;
  } catch {
    return fallback;
  }
}

/** 列显隐状态持久化到 localStorage，供各业务表复用 */
export function usePersistedColumnVisibility(
  storageKey: string,
  defaultVisibility: VisibilityState = {},
  forcedVisibleColumnIds: string[] = [],
) {
  const forcedKey = forcedVisibleColumnIds.join("|");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    typeof window === "undefined"
      ? defaultVisibility
      : readVisibility(storageKey, defaultVisibility, forcedVisibleColumnIds),
  );

  useEffect(() => {
    const next = { ...columnVisibility };
    for (const id of forcedVisibleColumnIds) {
      next[id] = true;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore quota / private mode
    }
  }, [storageKey, columnVisibility, forcedKey, forcedVisibleColumnIds]);

  useEffect(() => {
    setColumnVisibility((current) => {
      let changed = false;
      const next = { ...current };
      for (const id of forcedVisibleColumnIds) {
        if (next[id] !== true) {
          next[id] = true;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [forcedKey, forcedVisibleColumnIds]);

  return [columnVisibility, setColumnVisibility] as const;
}
